import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, Check } from 'lucide-react';

const Step4Interests = ({ interests, onUpdate, onNext, onBack }) => {
  const [selectedInterests, setSelectedInterests] = useState(interests);

  const interestCategories = [
    'Advice', 'Animation', 'Art', 'Blogging',
    'Comedy', 'Comics', 'Commissions',
    'Community', 'Cosplay', 'Crafts',
    'Dance & Theatre', 'Design', 'Drawing & Painting',
    'Education', 'Food & Drink', 'Fundraising',
    'Game Development', 'Gaming', 'Health & Fitness',
    'Lifestyle', 'Money', 'Music', 'News', 'NSFW',
    'Other', 'Photography', 'Podcast',
    'Science & Tech', 'Social', 'Software',
    'Spirituality', 'Streaming', 'Video and Film',
    'Writing'
  ];

  const handleInterestToggle = (interest) => {
    const newInterests = selectedInterests.includes(interest)
      ? selectedInterests.filter(i => i !== interest)
      : [...selectedInterests, interest];
    
    setSelectedInterests(newInterests);
    onUpdate({ interests: newInterests });
  };

  const handleContinue = () => {
    if (selectedInterests.length > 0) onNext();
  };

  return (
    <StepContainer>
      <StepCard>
        <StepHeader>
          <StepTitle>Choose your interests</StepTitle>
          <StepSubtitle>Pick the categories that best match what you do. You can update them anytime!</StepSubtitle>
        </StepHeader>

        <InterestsContainer>
          <InterestsGrid>
            {interestCategories.map((interest, index) => (
              <motion.div
                key={interest}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.02 }}
              >
                <InterestItem
                  $selected={selectedInterests.includes(interest)}
                  onClick={() => handleInterestToggle(interest)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {interest}
                  {selectedInterests.includes(interest) && (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 500, damping: 30 }}>
                      <Check size={16} />
                    </motion.div>
                  )}
                </InterestItem>
              </motion.div>
            ))}
          </InterestsGrid>
        </InterestsContainer>

        <SelectionSummary>
          <SummaryText>
            {selectedInterests.length === 0 
              ? 'No interests selected yet' 
              : `${selectedInterests.length} interest${selectedInterests.length === 1 ? '' : 's'} selected`
            }
          </SummaryText>
          {selectedInterests.length > 0 && (
            <SelectedTags>
              {selectedInterests.slice(0, 5).map(interest => (
                <Tag key={interest}>{interest}</Tag>
              ))}
              {selectedInterests.length > 5 && (
                <MoreTag>+{selectedInterests.length - 5} more</MoreTag>
              )}
            </SelectedTags>
          )}
        </SelectionSummary>

        <ButtonContainer>
          <BackButton onClick={onBack} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <ArrowLeft size={20} /> Back
          </BackButton>
          
          <NextButton
            onClick={handleContinue}
            disabled={selectedInterests.length === 0}
            $disabled={selectedInterests.length === 0}
            whileHover={{ scale: selectedInterests.length > 0 ? 1.05 : 1 }}
            whileTap={{ scale: selectedInterests.length > 0 ? 0.95 : 1 }}
          >
            Next
          </NextButton>
        </ButtonContainer>
      </StepCard>
    </StepContainer>
  );
};

const StepContainer = styled.div`width: 100%; max-width: 800px; display: flex; justify-content: center;`;

const StepCard = styled.div`
  background: #111111;
  border: 1px solid #333333;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  width: 100%;
`;

const StepHeader = styled.div`text-align: center; margin-bottom: 3rem;`;

const StepTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: bold;
  color: #ffffff;
  margin: 0 0 1rem 0;
`;

const StepSubtitle = styled.p`
  font-size: 1.1rem;
  color: #cccccc;
  max-width: 600px;
  margin: 0 auto;
`;

const InterestsContainer = styled.div`margin-bottom: 2rem;`;

const InterestsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  max-height: 400px;
  overflow-y: auto;
  padding: 1rem;
  border: 2px solid #333333;
  border-radius: 15px;
  background: #111111;
  
  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-track { background: #1a1a1a; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: #333333; border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: #e0c16f; }
`;

const InterestItem = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: ${props => props.$selected ? 'rgba(224, 193, 111, 0.1)' : '#1a1a1a'};
  color: ${props => props.$selected ? '#e0c16f' : '#cccccc'};
  border: 2px solid ${props => props.$selected ? '#e0c16f' : '#333333'};
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: left;
  min-height: 60px;
  
  &:hover {
    border-color: #e0c16f;
    background: rgba(224, 193, 111, 0.1);
    color: #e0c16f;
  }
  
  svg {
    color: #e0c16f;
    flex-shrink: 0;
  }
`;

const SelectionSummary = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #1a1a1a;
  border-radius: 12px;
  border: 2px solid #333333;
`;

const SummaryText = styled.p`
  font-size: 1rem;
  color: #ffffff;
  margin: 0 0 1rem 0;
  font-weight: 500;
`;

const SelectedTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: center;
`;

const Tag = styled.span`
  padding: 0.5rem 1rem;
  background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%);
  color: #000000;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const MoreTag = styled.span`
  padding: 0.5rem 1rem;
  background: #333333;
  color: #ffffff;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
`;

const ButtonContainer = styled.div`display: flex; gap: 1rem;`;

const BackButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: #1a1a1a;
  color: #cccccc;
  border: 2px solid #333333;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2a2a2a;
    border-color: #555555;
  }
`;

const NextButton = styled(motion.button)`
  flex: 1;
  padding: 1rem 2rem;
  background: ${props => props.$disabled ? '#333333' : 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)'};
  color: ${props => props.$disabled ? '#666666' : '#000000'};
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.$disabled ? '#333333' : 'linear-gradient(95.36deg, #bd851b 5.18%, #e0c16f 93.21%)'};
  }
`;

export default Step4Interests;